import {
  JSNS,
  type JsonObject,
  type DataSource,
  type DataSourceRef,
  type DataSourceType,
  type DataSourceUsage,
  type ImportStatus,
} from '@zimbra-webif/types';
import type { ZimbraClient } from '../client';

/** All data-source wire element keys (the type discriminators). */
const DS_TYPES: readonly DataSourceType[] = [
  'imap',
  'pop3',
  'caldav',
  'cal',
  'rss',
  'gal',
  'yab',
  'unknown',
];

/**
 * A new (unsaved) data source — `id` is server-assigned on create.
 *
 * `type` is re-asserted explicitly: `DataSource` carries an open index
 * signature, so `Omit` alone would widen away the required discriminator.
 */
export type NewDataSource = Omit<DataSource, 'id'> & { id?: string; type: DataSourceType };

/** Test result for a single data source. */
export interface TestDataSourceResult {
  success: boolean;
  error?: string;
}

/**
 * External data sources (urn:zimbraMail).
 *
 * A data source is an external account (POP3/IMAP/CalDAV/RSS/…) that Zimbra
 * polls and imports into a local folder. On the wire each source is wrapped in
 * a TYPE-DISCRIMINATED element (`<imap>`/`<pop3>`/`<caldav>`/`<cal>`/`<rss>`/…);
 * the SDK's `type` field drives which element is emitted, and parses responses
 * back out of those same keys (re-attaching `type` to each parsed object).
 */
export class DataSourcesApi {
  constructor(private readonly client: ZimbraClient) {}

  /** Strip the `type` discriminator from a source so it becomes the wire body. */
  private toWire(ds: Partial<DataSource> & { type: DataSourceType }): JsonObject {
    const { type, ...rest } = ds;
    return { [type]: rest as JsonObject };
  }

  /** Pull every typed key out of a response object into a flat, type-tagged list. */
  private fromWire<T extends JsonObject>(res: JsonObject): Array<T & { type: DataSourceType }> {
    const out: Array<T & { type: DataSourceType }> = [];
    for (const type of DS_TYPES) {
      const v = res[type];
      if (v === undefined) continue;
      const entries = Array.isArray(v) ? v : [v];
      for (const e of entries) {
        out.push({ ...(e as T), type });
      }
    }
    return out;
  }

  /** GetDataSources (urn:zimbraMail) — list all configured data sources. */
  async getDataSources(): Promise<DataSource[]> {
    const res = await this.client.call<JsonObject>(JSNS.mail, 'GetDataSources', {});
    return this.fromWire<DataSource>(res);
  }

  /** CreateDataSource (urn:zimbraMail) — create a source; returns it with its new id. */
  async createDataSource(ds: NewDataSource): Promise<DataSource> {
    const res = await this.client.call<JsonObject>(
      JSNS.mail,
      'CreateDataSource',
      this.toWire(ds),
    );
    return this.fromWire<DataSource>(res)[0] ?? ({ type: ds.type } as DataSource);
  }

  /** ModifyDataSource (urn:zimbraMail) — update changed fields of a source by id. */
  async modifyDataSource(
    ds: Partial<DataSource> & { id: string; type: DataSourceType },
  ): Promise<void> {
    await this.client.call(JSNS.mail, 'ModifyDataSource', this.toWire(ds));
  }

  /** DeleteDataSource (urn:zimbraMail) — delete one or more sources by name/id. */
  async deleteDataSource(refs: DataSourceRef[]): Promise<void> {
    const body: JsonObject = {};
    for (const ref of refs) {
      const { type, ...rest } = ref;
      const entry = rest as JsonObject;
      const existing = body[type];
      if (Array.isArray(existing)) {
        existing.push(entry);
      } else if (existing) {
        body[type] = [existing, entry];
      } else {
        body[type] = entry;
      }
    }
    await this.client.call(JSNS.mail, 'DeleteDataSource', body);
  }

  /** TestDataSource (urn:zimbraMail) — test connectivity without saving. */
  async testDataSource(ds: NewDataSource): Promise<TestDataSourceResult> {
    const res = await this.client.call<JsonObject>(
      JSNS.mail,
      'TestDataSource',
      this.toWire(ds),
    );
    const parsed = this.fromWire<{ success?: boolean; error?: string }>(res)[0];
    return {
      success: parsed?.success ?? false,
      ...(parsed?.error !== undefined ? { error: parsed.error } : {}),
    };
  }

  /** ImportData (urn:zimbraMail) — trigger an import now (async; poll status). */
  async importData(refs: DataSourceRef[]): Promise<void> {
    const body: JsonObject = {};
    for (const ref of refs) {
      const { type, ...rest } = ref;
      const entry = rest as JsonObject;
      const existing = body[type];
      if (Array.isArray(existing)) {
        existing.push(entry);
      } else if (existing) {
        body[type] = [existing, entry];
      } else {
        body[type] = entry;
      }
    }
    await this.client.call(JSNS.mail, 'ImportData', body);
  }

  /** GetImportStatus (urn:zimbraMail) — poll per-source import progress. */
  async getImportStatus(): Promise<ImportStatus[]> {
    const res = await this.client.call<JsonObject>(JSNS.mail, 'GetImportStatus', {});
    return this.fromWire<ImportStatus>(res);
  }

  /** GetDataSourceUsage (urn:zimbraMail) — per-source quota usage + totals. */
  async getDataSourceUsage(): Promise<{
    usages: DataSourceUsage[];
    dataSourceQuota: number;
    totalQuota: number;
  }> {
    const res = await this.client.call<{
      dataSourceUsage?: DataSourceUsage | DataSourceUsage[];
      dsQuota?: number | { _content?: number };
      totalQuota?: number | { _content?: number };
    }>(JSNS.mail, 'GetDataSourceUsage', {});
    const usage = res.dataSourceUsage;
    const usages = usage === undefined ? [] : Array.isArray(usage) ? usage : [usage];
    const num = (v: number | { _content?: number } | undefined): number =>
      typeof v === 'number' ? v : (v?._content ?? 0);
    return {
      usages,
      dataSourceQuota: num(res.dsQuota),
      totalQuota: num(res.totalQuota),
    };
  }
}
